import { useQuery } from "@apollo/client";
import { withJsonFormsControlProps } from "@jsonforms/react";
import CloseIcon from "@mui/icons-material/Close";
import { Checkbox, Dialog, DialogContent, DialogTitle, Grid, Radio } from "@mui/material";
import { Button, InputItem, LoadingSpinner } from "pepsico-ds";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { CampaignSetupContext } from "../../../context/CampaignSetupContext";
import { GlobalConfigContext } from "../../../context/GlobalConfigContext";
import { listCampaignsSweepstakesIds } from "../../../graphql/queries/getCampaignListing";
import { listSweepstakesActive } from "../../../graphql/queries/listSweepstakesActive";
import { convertDateToLocaleTz } from "../../../utils/commons";
import DatePickerMaterialUI from "../../common/DatePickerMaterialUI/DatePickerMaterialUI";
import "./SweepstakesOutcomeSelect.scss";

const SweepstakesOutcomeSelect = (props) => {
  const { handleChange, path, data, uischema, schema } = props || {};
  const { programTimezone } = useContext(GlobalConfigContext);
  const {
    campaignState: { activeStartDate, activeEndDate, id: currentCampaignId },
  } = useContext(CampaignSetupContext);

  const isMultipleAllowed = uischema?.options?.multiple !== false;
  const isSchemaTypeString = !isMultipleAllowed && schema?.type === "string";

  const { data: queryData, loading } = useQuery(listSweepstakesActive, {
    variables: {
      filter: {
        page: 0,
        size: 1000,
      },
    },
  });
  const { data: campaignsData, loading: campaignsLoading } = useQuery(
    listCampaignsSweepstakesIds
  );

  const [selectedIds, setSelectedIds] = useState([]);
  const [filters, setFilters] = useState({
    title: "",
    startsAt: "",
    endsAt: "",
  });
  const [showModal, setShowModal] = useState(false);
  const pendingIdRef = useRef(null);

  useEffect(() => {
    if (Array.isArray(data)) {
      setSelectedIds(data);
    } else if (data) {
      // in case a single id was previously saved
      setSelectedIds([data]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const campaignSweepstakesIds = useMemo(() => {
    return campaignsData?.listCampaigns?.length
      ? campaignsData.listCampaigns
          .filter((c) => Array.isArray(c.sweepstakes) && c.id !== currentCampaignId)
          .flatMap((c) => c.sweepstakes)
      : [];
  }, [campaignsData, currentCampaignId]);

  const allItems = useMemo(() => {
    const items = queryData?.listSweepstakesLatest?.items || [];
    // Only Draft and Active
    // exclude sweepstakes already attached to any campaign
    return items.filter(
      (it) =>
        ["CREATED", "ACTIVE"].includes(it?.status) &&
        !campaignSweepstakesIds.includes(it.id)
    );
  }, [queryData, campaignSweepstakesIds]);

  const visibleItems = useMemo(() => {
    return allItems.filter((it) => {
      if (filters.title) {
        const t = filters.title.toLowerCase();
        if (!it.title?.toLowerCase().includes(t)) return false;
      }
      if (filters.startsAt) {
        try {
          if (new Date(it.startsAt).getTime() < new Date(filters.startsAt).getTime())
            return false;
        } catch (e) {
          console.log(e);
        }
      }
      if (filters.endsAt) {
        try {
          if (new Date(it.endsAt).getTime() > new Date(filters.endsAt).getTime())
            return false;
        } catch (e) {
          console.log(e);
        }
      }
      return true;
    });
  }, [allItems, filters]);

  const isOutsideCampaignRange = (item) => {
    try {
      const itemStart = new Date(item.startsAt).getTime();
      const itemEnd = new Date(item.endsAt).getTime();
      const campStart = new Date(activeStartDate).getTime();
      const campEnd = new Date(activeEndDate).getTime();
      return itemEnd > campEnd || itemStart < campStart;
    } catch (e) {
      console.log(e);
      return false;
    }
  };

  const onToggle = (item) => {
    const exists = selectedIds.includes(item.id);
    if (exists) {
      const finalIds = selectedIds.filter((id) => id !== item.id);
      setSelectedIds(finalIds);
      try {
        if (isSchemaTypeString) {
          handleChange?.(path, "");
        } else {
          handleChange?.(path, finalIds);
        }
      } catch (e) {
        console.log(e);
      }
      return;
    }

    if (isOutsideCampaignRange(item)) {
      pendingIdRef.current = item.id;
      setShowModal(true);
      return;
    }

    if (isMultipleAllowed) {
      const finalIds = [...selectedIds, item.id];
      setSelectedIds(finalIds);
      try {
        handleChange?.(path, finalIds);
      } catch (e) {
        console.log(e);
      }
    } else {
      const finalValue = isSchemaTypeString ? item.id : [item.id];
      setSelectedIds([item.id]);
      try {
        handleChange?.(path, finalValue);
      } catch (e) {
        console.log(e);
      }
    }
  };

  const onConfirm = () => {
    if (!pendingIdRef.current) return;

    if (isMultipleAllowed) {
      const finalIds = [...selectedIds, pendingIdRef.current];
      setSelectedIds(finalIds);
      try {
        handleChange?.(path, finalIds);
      } catch (e) {
        console.log(e);
      }
    } else {
      const finalValue = isSchemaTypeString
        ? pendingIdRef.current
        : [pendingIdRef.current];
      setSelectedIds([pendingIdRef.current]);
      try {
        handleChange?.(path, finalValue);
      } catch (e) {
        console.log(e);
      }
    }

    setShowModal(false);
    pendingIdRef.current = null;
  };

  return (
    <div className="sweepstakes-outcome-select-wrap">
      <div className="display-flex align-center mb-3">
        <h4 className="font-semibold mr-3">
          Add Sweepstakes Outcomes:{" "}
          <span className="font-xs font-regular">
            {convertDateToLocaleTz(activeStartDate, programTimezone)} -{" "}
            {convertDateToLocaleTz(activeEndDate, programTimezone)}
          </span>
        </h4>
      </div>

      <div className="mt-3 display-flex align-center gap-3">
        <div className="container search-filter-sweepstakes-container p-0">
          <div className="search-wrapper">
            <InputItem
              placeholder="Type a search keyword..."
              trailingIcon="search"
              size="medium"
              className="input-search"
              onUpdate={(v) => setFilters((f) => ({ ...f, title: v || "" }))}
            />
          </div>
          <div className="filter-wrapper">
            <DatePickerMaterialUI
              setDate={(date) =>
                setFilters((f) => ({
                  ...f,
                  startsAt: date ? new Date(date).toISOString() : "",
                }))
              }
              dateValue={filters.startsAt}
              placeholder="Start date"
              timezone={programTimezone || "America/Chicago"}
            />
            <DatePickerMaterialUI
              setDate={(date) =>
                setFilters((f) => ({
                  ...f,
                  endsAt: date ? new Date(date).toISOString() : "",
                }))
              }
              dateValue={filters.endsAt}
              placeholder="End date"
              timezone={programTimezone || "America/Chicago"}
            />
            <div
              className="clear-all-btn"
              onClick={() => setFilters({ title: "", startsAt: "", endsAt: "" })}
              role="button"
              tabIndex="0"
            >
              Clear All
            </div>
          </div>
        </div>
      </div>

      <div>
        <table className="sweepstakes-list-table">
          <thead>
            <tr>
              <th></th>
              <th>TITLE</th>
              <th>DATE RANGE</th>
              <th>STATUS</th>
            </tr>
          </thead>
          <tbody>
            {(loading || campaignsLoading) && (
              <tr>
                <td colSpan="4" className="text-center">
                  <LoadingSpinner size={{ height: "40px", width: "40px" }} />
                </td>
              </tr>
            )}
            {!loading && visibleItems.length === 0 && (
              <tr>
                <td colSpan="4" className="font-bold text-center">
                  No sweepstakes found
                </td>
              </tr>
            )}
            {visibleItems.map((item) => (
              <tr key={item.id}>
                <td>
                  {isMultipleAllowed ? (
                    <Checkbox
                      checked={selectedIds.includes(item.id)}
                      onChange={() => onToggle(item)}
                      size="small"
                    />
                  ) : (
                    <Radio
                      checked={selectedIds.includes(item.id)}
                      onChange={() => onToggle(item)}
                      size="small"
                    />
                  )}
                </td>
                <td>
                  <p className="wrap-sweepstake-title" title={item.title}>
                    {item.title}
                  </p>
                </td>
                <td>
                  {item?.startsAt
                    ? convertDateToLocaleTz(item.startsAt, programTimezone, false)
                    : "-"}{" "}
                  -{" "}
                  {item?.endsAt
                    ? convertDateToLocaleTz(item.endsAt, programTimezone, false)
                    : "-"}
                </td>
                <td>{item.status}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Dialog
        className="create-winning-schedules-modal"
        open={showModal}
        scroll="paper"
      >
        <DialogTitle>
          <Grid container justifyContent="space-between" direction="row">
            <Grid item xs={10}>
              <h2>Confirm to continue</h2>
            </Grid>
            <Grid item xs={1} display="flex" justifyContent="flex-end">
              <CloseIcon
                onClick={() => setShowModal(false)}
                className="cursor-pointer"
              />
            </Grid>
          </Grid>
        </DialogTitle>
        <DialogContent sx={{ overflowX: "hidden" }}>
          <div className="my-5">
            <h5>Sweepstakes period falls outside of campaign start or end date.</h5>
            <p className="font-xs my-2">
              Campaign date:{" "}
              {convertDateToLocaleTz(activeStartDate, programTimezone)} -{" "}
              {convertDateToLocaleTz(activeEndDate, programTimezone)}
            </p>
          </div>
          <div className="mt-5 display-flex justify-content-end align-items-center">
            <Button
              onClick={() => setShowModal(false)}
              variant="tertiary"
              className="mr-3"
              size="medium"
            >
              Cancel
            </Button>
            <Button variant="secondary" onClick={onConfirm} size="medium">
              Confirm
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export const SweepstakesOutcomeSelectControl = withJsonFormsControlProps(
  SweepstakesOutcomeSelect
);
export default SweepstakesOutcomeSelect;
